import { useIsScheduledConsultActive } from '@/components/Patient/hooks/useIsScheduledConsultActive';
import { FF_C4C_ENABLE_CONNECT_VIDEO_CONSULT, FF_C4C_VIDEO_CONSULT } from '@/constants/featureFlags';
import { useFeatureFlags } from '@/hooks';
import type { Consultation } from '@/hooks/patients/usePatientConsultations';
import { Logger } from '@/utils/logger';
import type { ConsultationLinkCard } from '@montugroup/clinic-telecom';
import { TelecomWaitingRoomEntryStatus, triggerSourcePatientTelecomAttributeEnum } from '@montugroup/pms-api-contracts';
import type { ComponentProps } from 'react';
import { useSavePatientTelecomAttributes } from '../../../hooks/telecom/useSavePatientTelecomAttributes';
import { useNextConsultation } from './useNextConsultation';

const logger = new Logger('PMS:useConsultationLink');

export interface UseConsultationLinkProps {
  consultations: Consultation[];

  consultationStarted?: boolean;
  setConsultationStarted: (started: boolean) => void;
  showConsultation?: Consultation | null;
  setShowConsultation?: (consultation: Consultation) => void;

  patientId: number;
  patientName: string;
  patientCode?: string;
  patientPhoneNumber?: string;
}

export interface UseConsultationLinkReturn {
  consultationLinkProps?: ComponentProps<typeof ConsultationLinkCard>;
}

export const useConsultationLink = ({
  consultations,

  consultationStarted,
  setConsultationStarted,
  showConsultation,
  setShowConsultation,

  patientId,
  patientName,
  patientCode,
  patientPhoneNumber,
}: UseConsultationLinkProps): UseConsultationLinkReturn => {
  const { flags } = useFeatureFlags();
  const isVideoConsultEnabled = flags[FF_C4C_VIDEO_CONSULT];
  const isConnectVideoConsultEnabled = flags[FF_C4C_ENABLE_CONNECT_VIDEO_CONSULT];
  const isScheduledConsult = useIsScheduledConsultActive();

  const nextConsultation = useNextConsultation({ consultations });
  const showConsultationLinkCard = isVideoConsultEnabled && isConnectVideoConsultEnabled;
  const showConsultationLinkCardV3 = showConsultationLinkCard && !!nextConsultation;

  const { mutateAsync: savePatientTelecomAttributes } = useSavePatientTelecomAttributes(
    patientId,
    nextConsultation?.id ?? showConsultation?.id,
  );

  const handleContact = async (contact: connect.Contact) => {
    try {
      await savePatientTelecomAttributes(
        contact.contactId,
        undefined,
        triggerSourcePatientTelecomAttributeEnum.enum.consultationVideoCallAttributes,
      );
    } catch (error) {
      logger.error('Failed to save patient telecom attributes', error);
    }
  };

  if (showConsultationLinkCardV3) {
    return {
      consultationLinkProps: {
        label: !isScheduledConsult ? nextConsultation.consultation_code : undefined,
        consultationStarted,
        setConsultationStarted,
        patientId: `${patientId}`,
        patientName,
        patientPhoneNumber,
        patientCode,
        waitingRoomStatus: nextConsultation.waiting_room_status ?? TelecomWaitingRoomEntryStatus.Created,
        consultationId: nextConsultation.id,
        consultationCode: nextConsultation.consultation_code,
        onContact: handleContact,
        onShowConsultation: !isScheduledConsult ? () => setShowConsultation?.(nextConsultation) : undefined,
      },
    };
  }

  return {};
};
